import React, { useState, useEffect } from 'react';
import { Row, Col, Badge, Tabs, Tab, Button } from 'react-bootstrap';
import Layout from '../../components/layouts/Main';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import { PulseLoader } from "react-spinners";
import { formatDateTime } from '../../utils/dateHelpers';
import StockTable from '../../components/stock/StockTable';
import SalesOrdersOverview from '../../components/stock/SalesOrdersOverview';
import ProductionNotifications from '../../components/stock/ProductionNotifications';
// import StockAlerts from '../../components/stock/StockAlerts';
import { FiDownload } from 'react-icons/fi';
import ReportDownloader from '../../components/common/ReportDownloader';

const StockOverview = () => {
    const [stockData, setStockData] = useState({
        products: [],
        lastUpdated: null
    });
    const [isLoading, setIsLoading] = useState(true);
    const [showExportModal, setShowExportModal] = useState(false);
    const [salesOrders, setSalesOrders] = useState({
        new: [],
        inProduction: [],
        dispatched: []
    });
    const [reservedStock, setReservedStock] = useState({});
    const [inProductionStock, setInProductionStock] = useState({});
    const [notifications, setNotifications] = useState([]);

    // Group products by their groupId
    const groupedProducts = stockData.products.reduce((acc, product) => {
        const groupId = product.product.groupId?._id || 'ungrouped';
        const groupName = product.product.groupId?.groupName || 'Ungrouped';

        if (!acc[groupId]) {
            acc[groupId] = {
                _id: groupId,
                groupName: groupName,
                products: []
            };
        }
        acc[groupId].products.push(product);
        return acc;
    }, {});

    const getStockStatus = (availableStock, bufferStock) => {
        if (availableStock <= 0) {
            return { label: 'Out of Stock', color: 'danger' };
        }
        if (availableStock <= bufferStock) {
            return { label: 'Low Stock', color: 'warning' };
        }
        return { label: 'In Stock', color: 'success' };
    };

    useEffect(() => {
        fetchStockData();
        fetchSalesOrders();
    });

    const fetchStockData = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${config.API_BASE_URL}/inventory/stock-overview`);
            setStockData({
                products: response.data.products,
                lastUpdated: response.data.lastUpdated
            });
            setReservedStock(response.data.reservedStock || {});
            setInProductionStock(response.data.inProductionStock || {});
        } catch (error) {
            console.error('Error fetching stock data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSalesOrders = async () => {
        try {
            const response = await axios.get(`${config.API_BASE_URL}/sales-orders?populateItems=true&populateBatch=true`);
            const allOrders = response.data;

            const categorizedOrders = {
                draft: allOrders.filter(o => o.status === 'Draft'),
                new: allOrders.filter(o => ['Created', 'Confirmed', 'Stock Pending', 'PO Received'].includes(o.status)),
                inProduction: allOrders.filter(o => ['In Production', 'Production Requested', 'Production Complete'].includes(o.status) || o.productionBatch),
                dispatched: allOrders.filter(o => ['Partially Dispatched', 'Fully Dispatched', 'In Transit', 'Partially Delivered', 'Delivered', 'Completed'].includes(o.status))
            };

            setSalesOrders(categorizedOrders);
            
            // Check for completed production batches
            checkCompletedProductions(categorizedOrders.inProduction);
        } catch (error) {
            console.error('Error fetching sales orders:', error);
        }
    };

    const checkCompletedProductions = (productionOrders) => {
        const completedNotifications = [];
        productionOrders.forEach(order => {
            if (order.productionBatch?.status === 'Completed') {
                completedNotifications.push({
                    id: order._id,
                    type: 'production_completed',
                    message: `Production completed for Sales Order ${order.orderNumber}`,
                    orderId: order._id,
                    batchId: order.productionBatch._id
                });
            }
        });
        setNotifications(completedNotifications);
    };

    if (isLoading) {
        return (
            <Layout>
                <div className="text-center py-5">
                    <PulseLoader color="#014b7cbb" size={9} />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
                    <li className="breadcrumb-item active">Stock Overview</li>
                </ol>
            </nav>

            <Row className="mb-3">
                <Col>
                    <h2>Stock Overview</h2>
                </Col>
                <Col className="text-end">
                    <div className="d-flex justify-content-end align-items-center gap-2">
                        <Button variant="outline-primary" size="sm" onClick={() => setShowExportModal(true)}>
                            <FiDownload className="me-2" /> Download Stock Report
                        </Button>
                        <small className="text-muted">
                            Last Updated: {formatDateTime(stockData.lastUpdated)}
                        </small>
                    </div>
                </Col>
            </Row>

            <ProductionNotifications 
                notifications={notifications} 
                onDismiss={() => setNotifications([])} 
            />

            <Tabs defaultActiveKey="stock" className="mb-3">
                <Tab eventKey="stock" title="Stock Status">
                    <Row>
                        <Col md={12}>
                            <StockTable 
                                groupedProducts={groupedProducts} 
                                reservedStock={reservedStock}
                                getStockStatus={getStockStatus}
                            />
                        </Col>
                        {/* <Col md={4}>
                            <StockAlerts 
                                products={stockData.products} 
                                reservedStock={reservedStock} 
                            />
                        </Col> */}
                    </Row>
                </Tab>
                <Tab eventKey="orders" title={
                    <div className="d-flex align-items-center gap-2">
                        Sales Orders
                        <Badge bg="secondary">
                            {salesOrders.new.length + salesOrders.inProduction.length + salesOrders.dispatched.length}
                        </Badge>
                    </div>
                }>
                    <SalesOrdersOverview orders={salesOrders} />
                </Tab>
            </Tabs>

            <ReportDownloader 
                show={showExportModal}
                handleClose={() => setShowExportModal(false)}
                data={stockData.products.map(item => {
                    const productId = item.product?._id;
                    const totalPacked = (item.packedStock || []).reduce((sum, s) => sum + (s.availableStock || 0), 0);
                    const totalUnpacked = (item.unpackedStock || []).reduce((sum, s) => sum + (s.availableStock || 0), 0);
                    const totalInPacking = (item.inPackingStock || []).reduce((sum, s) => sum + (s.availableStock || 0), 0);
                    const totalInProduction = inProductionStock[productId] || 0;
                    const totalCombinedAvailable = totalPacked + totalUnpacked + totalInPacking;
                    const reserved = reservedStock[productId] || 0;
                    const availableAfterReserved = totalCombinedAvailable - reserved;
                    const buffer = item.product?.bufferStock || 0;

                    let stockStatus = 'In Stock';
                    if (totalCombinedAvailable <= 0) stockStatus = 'Out of Stock';
                    else if (totalCombinedAvailable <= buffer) stockStatus = 'Low Stock';

                    return {
                        ...item,
                        totalPacked,
                        totalUnpacked,
                        totalInPacking,
                        totalInProduction,
                        totalCombinedAvailable,
                        reservedStock: reserved,
                        availableAfterReserved,
                        stockStatus
                    };
                })}
                availableColumns={[
                    { header: 'Product Name', key: 'product.productName' },
                    { header: 'Product Group', key: 'product.groupId.groupName' },
                    { header: 'Category', key: 'product.groupId.groupName' },
                    { header: 'Total Available Stock', key: 'totalCombinedAvailable' },
                    { header: 'Packed Stock', key: 'totalPacked' },
                    { header: 'Unpacked Stock', key: 'totalUnpacked' },
                    { header: 'In Packing Operations', key: 'totalInPacking' },
                    { header: 'In Production', key: 'totalInProduction' },
                    { header: 'Reserved Stock (for SOs)', key: 'reservedStock' },
                    { header: 'Available After Reserved', key: 'availableAfterReserved' },
                    { header: 'Min Stock (Buffer)', key: 'product.bufferStock' },
                    { header: 'Stock Status', key: 'stockStatus' }
                ]}
                title="Export Total Inventory"
                fileName="total_inventory"
            />

        </Layout>
    );
};

export default StockOverview;